import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Chat } from '@progress/kendo-react-conversational-ui'
import { IntlProvider } from '@progress/kendo-react-intl'
import './chatBoxGL.css'
import { useTranslation } from 'react-i18next'
import { newProfileIcon } from '../../../assets/logos/icons'

/**
 * @typedef {object} Props
 * @property {import('@progress/kendo-react-conversational-ui').User} user
 * @property {string} [placeholderChat]
 * @property {number} [widthChat]
 * @property {Function} createMessage
 * @property {object[]} conversation
 * @property {boolean} [disabled]
 * @property {Array<{email: string, firstName: string, firstLastName: string, avatarUrl: string}>} [mentionMembers]
 * @property {Function} [onMarkAsRead] - Optional callback invoked once on mount to mark messages as read
 */

/**
 * @param {Props} props
 */
const ChatBoxGLPure = (props) => {
  const {
    user, //Array with users of the conversation
    placeholderChat, //PlaceHolder to input toolBar
    widthChat, //Width chat
    createMessage, //Function to create message, use message and file
    conversation, //Information of messages that get from getWorkFlowExecutionMessage
    disabled = false,
    heightChat = 500,
    mentionMembers, //Optional: array of members available to @mention
    onMarkAsRead, //Optional: called once on mount to mark messages as read
  } = props

  const { t } = useTranslation()
  /** @type {React.MutableRefObject<HTMLInputElement | null>} */
  const fileUpload = useRef(null)
  /** @type {React.MutableRefObject<HTMLDivElement | null>} */
  const chatContainerRef = useRef(null)
  /** @type {React.MutableRefObject<Function | undefined>} */
  const messageBoxRef = useRef()
  /** Tracks the index of the '@' char that opened the mention dropdown */
  const mentionAtIndexRef = useRef(-1)
  /** Flag to skip handleInput processing during artificial event dispatch */
  const suppressMentionDetectionRef = useRef(false)

  const [messages, setMessages] = useState(conversation)
  const [messagesActual, setMessagesActual] = useState({})
  const [fileContent, setFileContent] = useState("")

  // @mention feature state
  const [showMentionList, setShowMentionList] = useState(false)
  const [mentionFilter, setMentionFilter] = useState('')

  /** @type {boolean} */
  const hasMentionFeature = Array.isArray(mentionMembers)

  /** Value currently in the Kendo input — used to exclude already-mentioned members */
  const currentInputVal = hasMentionFeature
    ? (chatContainerRef.current?.querySelector('.k-message-box textarea, .k-message-box input')?.value || '')
    : ''

  /** @type {Array<{email: string, firstName: string, firstLastName: string, avatarUrl: string}>} */
  const filteredMentionMembers = hasMentionFeature
    ? (mentionMembers || []).filter(m => {
      if (m.memberTypeKey === 'weMemberGovo') return false
      if (m.email && currentInputVal.includes(m.email)) return false
      const q = mentionFilter.toLowerCase()
      const email = (m.email || '').toLowerCase()
      const fullName = `${m.firstName || ''} ${m.firstLastName || ''}`.toLowerCase()
      return email.includes(q) || fullName.includes(q)
    })
    : []

  /**
   * Injects a selected @mention into Kendo's native input via DOM APIs.
   * @param {{email: string, firstName: string, firstLastName: string}} member
   * @returns {void}
   */
  const selectMentionMember = (member) => {
    const email = member.email
    const input = chatContainerRef.current?.querySelector('.k-message-box textarea, .k-message-box input')
    if (!input) { setShowMentionList(false); return }

    // Use the saved @-index from when the dropdown was opened (avoids relying on selectionStart during mousedown)
    const atIndex = mentionAtIndexRef.current
    if (atIndex === -1) { setShowMentionList(false); return }

    const val = input.value
    const cursorPos = input.selectionStart
    // Strip any text already inserted from the @ position up to the cursor
    // (handles case where user opens dropdown again after a previous selection)
    const newText = val.slice(0, atIndex) + email + ' ' + val.slice(cursorPos)

    // Suppress handleInput from re-opening the dropdown during the artificial event
    suppressMentionDetectionRef.current = true

    // Update React's internal state for the controlled Kendo input
    const nativeProto = input.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype
    const nativeSetter = Object.getOwnPropertyDescriptor(nativeProto, 'value')?.set
    if (nativeSetter) {
      nativeSetter.call(input, newText)
    } else {
      input.value = newText
    }
    input.dispatchEvent(new Event('input', { bubbles: true }))

    suppressMentionDetectionRef.current = false
    mentionAtIndexRef.current = -1
    setShowMentionList(false)
    setMentionFilter('')
    setTimeout(() => {
      const newCursorPos = atIndex + email.length + 1
      input.focus()
      input.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }
  const handleUploadClick = () => {
    if (disabled) return
    if (fileUpload.current) fileUpload.current.click()
  }

  /** @param {import('@progress/kendo-react-conversational-ui').ChatMessageSendEvent} event */
  const addNewMessage = (event) => {
    if (disabled) return
    let messageData = event.message
    if (hasMentionFeature) {
      const text = event.message.text || ''
      /** @type {Array<{email: string}>} */
      const usersMentioned = []
        ; (mentionMembers || []).forEach(m => {
          if (m.email && text.includes(m.email) && !usersMentioned.find(u => u.email === m.email)) {
            usersMentioned.push({ email: m.email })
          }
        })
      messageData = { ...event.message, usersMentioned }
    }
    setMessages([...messages, messageData])
    setMessagesActual(messageData)
    setShowMentionList(false)
  }

  /**
   * @type {React.ChangeEventHandler<HTMLInputElement>}
   * @returns {void}
   */
  const handleInputChange = (e) => {
    if (disabled) return
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      const reader = new FileReader()

      reader.onloadend = (event) => {
        const message = {
          author: user,
          attachments: [{
            name: file.name,
            content: event.target?.result || '',
            contentType: 'application/pdf',
          }],
        }
        setMessages(prev => [...prev, message])
        setFileContent(file)
      }
      reader.readAsDataURL(file)
    }
  }

  //send message and document to function create message
  useEffect(() => {
    if (
      (messagesActual && messagesActual.author) ||
      (fileContent && fileContent.name)
    ) {
      createMessage(messagesActual, fileContent)
      setMessagesActual({})
      setFileContent("")
    }
  }, [messagesActual, fileContent, createMessage])

  useEffect(() => {
    if (onMarkAsRead) onMarkAsRead()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setMessages(conversation)
  }, [conversation])

  // Inject class into Kendo's .k-message-group for system messages so CSS can target them without :has()
  useEffect(() => {
    const applySystemClasses = () => {
      if (!chatContainerRef.current) return
      const systemMessages = chatContainerRef.current.querySelectorAll('.chatBoxGL-system-message')
      systemMessages.forEach(el => {
        const messageGroup = el.closest('.k-message-group')
        if (messageGroup) {
          messageGroup.classList.add('chatBoxGL-system-message-group')
        }
      })
    }
    applySystemClasses()
    const timer = setTimeout(applySystemClasses, 50)
    return () => clearTimeout(timer)
  }, [messages])

  // Attach @mention detection to Kendo's native input (runs once; stable StableMessageBox keeps the input alive)
  useEffect(() => {
    if (!hasMentionFeature) return

    const handleInput = (e) => {
      if (suppressMentionDetectionRef.current) return
      const val = e.target.value
      const cursorPos = e.target.selectionStart
      const textBeforeCursor = val.slice(0, cursorPos)
      const atIndex = textBeforeCursor.lastIndexOf('@')
      if (atIndex !== -1) {
        // Only treat '@' as a mention trigger if it's at the start or preceded by whitespace
        // This prevents the '@' inside an already-inserted email from being re-detected
        const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' '
        const isTrigger = charBeforeAt === ' ' || charBeforeAt === '\n'
        if (isTrigger) {
          const query = textBeforeCursor.slice(atIndex + 1)
          if (!query.includes(' ') && !query.includes('\n')) {
            mentionAtIndexRef.current = atIndex
            setMentionFilter(query)
            setShowMentionList(true)
            return
          }
        }
      }
      mentionAtIndexRef.current = -1
      setShowMentionList(false)
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowMentionList(false)
    }

    let attachedInput = null
    const attach = () => {
      const input = chatContainerRef.current?.querySelector('.k-message-box textarea, .k-message-box input')
      if (!input) return false
      input.addEventListener('input', handleInput)
      input.addEventListener('keydown', handleKeyDown)
      attachedInput = input
      return true
    }

    let timer
    if (!attach()) {
      timer = setTimeout(attach, 150)
    }

    return () => {
      clearTimeout(timer)
      if (attachedInput) {
        attachedInput.removeEventListener('input', handleInput)
        attachedInput.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [hasMentionFeature]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * @param {import('@progress/kendo-react-conversational-ui').ChatMessageTemplateProps} props
   */
  const MessageTemplate = (props) => {
    const isSystemMessage = props.item.memberTypeKey === 'weMemberGovo'

    const formattedTime = props.item.timestamp
      ? new Date(props.item.timestamp).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })
      : ''

    if (isSystemMessage) {
      // Build display text: for known message keys use metadata + translated action
      let systemText = props.item.text
      const knownKey = props.item.messageKey && `signatureDetail.${props.item.messageKey}`
      if (knownKey && props.item.messageMetadata?.user) {
        const action = t(knownKey, props.item.messageKey)
        systemText = `${props.item.messageMetadata.user} ${action}`
      }
      return systemText && systemText !== 'undefined' ? (
        <div className='chatBoxGL-system-message'>
          <div>{systemText}</div>
          {formattedTime ? <span className='chatBoxGL-system-message-time'>{formattedTime}</span> : null}
        </div>
      ) : null
    }

    return (
      <>
        {props.item.text && props.item.text !== 'undefined' ? (
          <div className='k-bubble'>
            <div>{props.item.text}</div>
            {formattedTime ? <span className='chatBoxGL-bubble-time'>{formattedTime}</span> : null}
          </div>
        ) : null}
      </>
    )
  }

  /*Message box when send file*/
  const AttachmentTemplate = (props) => {
    return (
      <div className='k-card k-card-type-rich chatBox-boxContainer-pdf'>
        {props.item && props.item.name ? (
          <div className='k-card-body quoteCard'>
            <a href={props.item.site} download>
              {props.item.name}
            </a>
          </div>
        ) : (
          <div>{''}</div>
        )}
      </div>
    )
  }

  /*Upload btns, send message and send file*/
  /**
   * @param {{icon: string}}
   */
  const UploadButton = ({ icon }) => {
    return (
      <button
        className={
          'k-button k-button-md k-rounded-md k-button-flat k-button-flat-base k-button k-button-md k-rounded-md k-button-solid k-button-solid-base-icon'
        }
        onClick={handleUploadClick}
        disabled={disabled}
      >
        <span
          className={'k-icon ' + icon}
          style={{
            fontSize: '20px',
          }}
        />
      </button>
    )
  }

  // Write the latest render into the ref on every render (captures current `disabled`)
  messageBoxRef.current = (msgBoxProps) => (
    <div style={{ opacity: disabled ? 0.2 : 1, pointerEvents: disabled ? 'none' : 'auto', display: 'flex', alignItems: 'center', width: '100%' }}>
      {msgBoxProps.messageInput}
      {msgBoxProps.sendButton}
    </div>
  )

  // Stable reference — Kendo never sees a new component, so it never unmounts/remounts the input
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const StableMessageBox = useCallback((msgBoxProps) => messageBoxRef.current(msgBoxProps), [])



  return (
    <div
      className='chatBoxGLPure-container'
      style={{ height: heightChat, position: 'relative' }}
      ref={chatContainerRef}
    >
      <input
        type='file'
        onChange={handleInputChange}
        style={{ display: 'none' }}
        ref={fileUpload}
        accept='application/pdf'
      />
      <IntlProvider locale='es'>
        <Chat
          user={user}
          messages={messages}
          onMessageSend={addNewMessage}
          placeholder={placeholderChat ?? t('chatBot.placeholder')}
          width={widthChat}
          attachmentTemplate={AttachmentTemplate}
          messageBox={StableMessageBox}
          messageTemplate={MessageTemplate}
          onActionExecute={disabled ? () => { } : createMessage}
        />
      </IntlProvider>
      {hasMentionFeature && showMentionList && filteredMentionMembers.length > 0 && (
        <ul className='chatBoxGL-mention-dropdown'>
          {filteredMentionMembers.map(member => (
            <li
              key={member.email}
              className='chatBoxGL-mention-item'
              onMouseDown={(e) => { e.preventDefault(); selectMentionMember(member) }}
            >
              <img
                src={member.avatarUrl || newProfileIcon}
                alt={`${member.firstName} ${member.firstLastName}`}
                className='chatBoxGL-mention-avatar'
                onError={(e) => { e.target.src = newProfileIcon }}
              />
              <div className='chatBoxGL-mention-info'>
                <span className='chatBoxGL-mention-name'>{member.firstName} {member.firstLastName}</span>
                <span className='chatBoxGL-mention-handle'>{member.email}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export { ChatBoxGLPure as default }
